import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  deleteOrder,
  editOrder,
  executeOrder,
  getOrderBookList,
  setOrderBookList,
  setOrderBookPage,
  setOrderBookSearchText,
  setOrderBookStatus,
} from "Store/Reducer/orderbook.slice";
import { getAccountCommonList, getSegmentOptionList, getSymbolList } from "Store/Reducer/common.slice";
import useMobileViewport from "hooks/useMobileViewport";
import MobileRecordCard from "components/mobile/MobileRecordCard";
import MobileRecordList from "components/mobile/MobileRecordList";
import { formatUiDateTime } from "utils/dateFormats";
import { resolveSegmentLabel } from "utils/segmentUtils";

export default function OrderBook() {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const {
    orderBookList,
    orderBookPendingLoading,
    totalRecordOrderBook,
    searchTextOrderBook,
    isCompletedOrderType,
    isOrderBookUpdated,
  } = useSelector((s) => s.orderbook);
  const { segmentsList } = useSelector((s) => s.common);
  const { symbolList, accountOptionList } = useSelector((s) => s.common);
  const { userRole } = useSelector((s) => s.auth);

  const canModify = useMemo(
    () => !["partner", "sub broker"].includes(String(userRole || "").toLowerCase()),
    [userRole]
  );
  const [segment, setSegment] = useState({ label: null, value: null });
  const [symbol, setSymbol] = useState({ label: null, value: null });
  const [client, setClient] = useState({ label: null, value: null });
  const [orderType, setOrderType] = useState("");
  const isSuperAdmin = String(userRole || "").toLowerCase() === "super admin";

  const loadOrders = useCallback(
    (extra = {}) => {
      const pageNo = extra.pageNo || 1;
      const params = {
        pageNo,
        pageSize: 50,
        searchText: extra.searchText ?? searchTextOrderBook,
        isCompleted: extra.status || isCompletedOrderType,
        appendData: !!extra.appendData,
        segment: extra.segment ?? segment,
        symbol: extra.symbol ?? symbol,
        client: extra.client ?? client,
      };
      dispatch(getOrderBookList(params));
      dispatch(setOrderBookPage(pageNo));
    },
    [dispatch, isCompletedOrderType, searchTextOrderBook, segment, symbol, client]
  );

  useEffect(() => {
    dispatch(getSegmentOptionList());
    dispatch(getAccountCommonList({ roleName: "User" }));
    loadOrders({ pageNo: 1 });
    return () => {
      dispatch(setOrderBookList([]));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [dispatch, loadOrders]);

  useEffect(() => {
    if (isOrderBookUpdated) {
      loadOrders({ pageNo: 1 });
    }
  }, [isOrderBookUpdated, loadOrders]);

  const onDelete = (id) => {
    const isBulk = Array.isArray(id);
    Swal.fire({
      title: "Are you sure?",
      text: isBulk
        ? "Are you sure you want to cancel all pending orders?"
        : "Are you sure you want to cancel this order?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isBulk ? "Yes, Cancel All" : "Yes, Cancel Order",
      cancelButtonText: "No",
      confirmButtonColor: "#885b24",
      cancelButtonColor: "#d33",
    }).then((r) => {
      if (r.isConfirmed) dispatch(deleteOrder({ id }));
    });
  };

  const onEditPrice = (order) => {
    Swal.fire({
      title: "Edit Order Price",
      input: "number",
      inputValue: order.rate,
      showCancelButton: true,
      confirmButtonText: "Update",
      inputValidator: (v) => (!v ? "Price is required" : null),
    }).then((res) => {
      if (res.isConfirmed) dispatch(editOrder({ order, price: res.value }));
    });
  };

  const onSearch = (e) => {
    const value = e.target.value;
    dispatch(setOrderBookSearchText(value));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => loadOrders({ pageNo: 1, searchText: value }), 350);
  };

  const onToggleStatus = (status) => {
    if (status === isCompletedOrderType) return;
    dispatch(setOrderBookStatus(status));
    loadOrders({ pageNo: 1, status, searchText: "" });
  };

  const onChangeSegment = (seg) => {
    setSegment(seg);
    setSymbol({ label: null, value: null });
    if (seg?.value) dispatch(getSymbolList({ id: seg.value }));
  };

  const onExecute = (order) => {
    Swal.fire({
      title: "Execute order?",
      text: "Are you sure to execute this order by market?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Execute",
      confirmButtonColor: "#885b24",
      cancelButtonColor: "#d33",
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(
          executeOrder({ id: order.id, displayName: order.displayName, segmentId: order.segmentId })
        );
      }
    });
  };

  const onFindOrder = () => {
    loadOrders({ pageNo: 1 });
  };

  const visibleRows = useMemo(() => {
    if (!orderType) return orderBookList;
    return orderBookList.filter((x) => String(x.executionTypeName || "").toLowerCase() === orderType);
  }, [orderBookList, orderType]);

  const isMobileViewport = useMobileViewport();

  return (
    <div
      className={`fx-book-page${isMobileViewport ? " fx-module--mobile-cards" : ""}`}
      style={{ padding: "16px 20px", background: "var(--bg-panel)", minHeight: "100%" }}
    >
      <div className="fx-book-page__header" style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, alignItems: "center" }}>
        <h3 style={{ margin: 0, color: "var(--green)" }}>Orders</h3>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>
          Total: {totalRecordOrderBook}
        </span>
      </div>

      <div
        className="fx-book-filter-row fx-book-filter-card"
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 10,
          padding: 10,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "var(--bg-secondary)",
        }}
      >
        <div className="fx-ob-summary" style={{ minWidth: 240, display: "flex", alignItems: "center", gap: 8 }}>
          <strong style={{ fontSize: 13 }}>Orders:</strong>
          <span>{totalRecordOrderBook}</span>
        </div>
        <div className="fx-ob-radio-row">
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input
            type="radio"
            checked={isCompletedOrderType === "pending"}
            onChange={() => onToggleStatus("pending")}
          />
          Pending Orders
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input
            type="radio"
            checked={isCompletedOrderType === "completed"}
            onChange={() => onToggleStatus("completed")}
          />
          Completed / History
        </label>
        </div>
        <select
          className="fx-book-select"
          value={segment?.value ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            if (!val) onChangeSegment({ label: null, value: null });
            else {
              const next = segmentsList.find((s) => String(s.value) === String(val));
              onChangeSegment(next || { label: null, value: null });
            }
          }}
          style={{ minWidth: 160, padding: "7px 8px", borderRadius: 6 }}
        >
          <option value="">All Segments</option>
          {segmentsList.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className="fx-book-select"
          value={symbol?.value ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            const next = symbolList.find((s) => String(s.value) === String(val));
            const mapped = val ? next || { label: val, value: val } : { label: null, value: null };
            setSymbol(mapped);
          }}
          style={{ minWidth: 170, padding: "7px 8px", borderRadius: 6 }}
        >
          <option value="">All Symbols</option>
          {symbolList.map((s, i) => (
            <option key={`${s.value}-${i}`} value={s.value}>
              {s.label || s.value}
            </option>
          ))}
        </select>
        {String(userRole || "").toLowerCase() !== "user" && (
          <select
            className="fx-book-select"
            value={client?.value ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              const next = accountOptionList.find((s) => String(s.value) === String(val));
              const mapped = val ? next || { label: val, value: val } : { label: null, value: null };
              setClient(mapped);
            }}
            style={{ minWidth: 160, padding: "7px 8px", borderRadius: 6 }}
          >
            <option value="">All Clients</option>
            {accountOptionList.map((s, i) => (
              <option key={`${s.value}-${i}`} value={s.value}>
                {s.label || s.value}
              </option>
            ))}
          </select>
        )}
        <select
          className="fx-book-select"
          value={orderType}
          onChange={(e) => setOrderType(e.target.value)}
          style={{ minWidth: 140, padding: "7px 8px", borderRadius: 6 }}
        >
          <option value="">All Types</option>
          <option value="buy limit">Buy Limit</option>
          <option value="buy stop">Buy Stop</option>
          <option value="sell limit">Sell Limit</option>
          <option value="sell stop">Sell Stop</option>
        </select>
        <button type="button" className="fx-book-btn" onClick={onFindOrder} style={{ padding: "7px 12px", fontWeight: 700 }}>
          Find Order
        </button>
      </div>

      <div
        className="fx-book-toolbar"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          padding: "8px 4px",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <strong style={{ color: "var(--text-primary)" }}>
          {isCompletedOrderType === "pending" ? "Pending Orders" : "Order History"}:{" "}
          {totalRecordOrderBook}
        </strong>
        <div className="fx-book-toolbar__actions" style={{ display: "flex", gap: 8 }}>
          {canModify && isCompletedOrderType === "pending" && orderBookList.length > 0 && (
            <button type="button" className="fx-book-btn" onClick={() => onDelete(orderBookList)} style={{ padding: "7px 12px" }}>
              Cancel All
            </button>
          )}
          <button type="button" className="fx-book-btn" onClick={() => loadOrders({ pageNo: 1 })} style={{ padding: "7px 12px" }}>
            Refresh
          </button>
        </div>
      </div>

      <div className="fx-book-search-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <input
          type="text"
          value={searchTextOrderBook}
          onChange={onSearch}
          placeholder="Search"
          className="fx-book-search-box"
          style={{ minWidth: 220, flex: "1 1 220px", padding: "7px 10px", borderRadius: 6, border: "1px solid #c9c9c9" }}
        />
        <strong style={{ alignSelf: "center" }}>Total Records: {totalRecordOrderBook}</strong>
      </div>

      {isMobileViewport ? (
        <MobileRecordList
          loading={orderBookPendingLoading}
          empty={!orderBookPendingLoading && visibleRows.length === 0}
          emptyText="No orders found"
          style={{ maxHeight: "64vh", overflowY: "auto" }}
        >
          {visibleRows.map((row) => {
            const isBuy = String(row.orderTypeName || "").toLowerCase() === "buy";
            return (
              <MobileRecordCard
                key={row.id}
                title={row.script}
                subtitle={[row.clientName, formatUiDateTime(row.tradeDateTime || row.addTime, "")].filter(Boolean).join(" · ")}
                badge={row.orderTypeName}
                badgeVariant={isBuy ? "buy" : "sell"}
                variant={isBuy ? "buy" : "sell"}
                fields={[
                  { label: "Segment", value: resolveSegmentLabel(row.segmentId, segmentsList, row.segment) },
                  { label: "Type", value: row.executionTypeName },
                  { label: "Lot", value: Number(row.lot || 0).toFixed(2) },
                  { label: "Qty", value: Number(row.qty || 0).toFixed(2) },
                  { label: "Price", value: Number(row.rate || 0).toFixed(2) },
                  { label: "Status", value: row.status },
                ]}
              >
                {canModify && row.status === "Pending" && (
                  <div className="pr-mobile-card__actions">
                    {isSuperAdmin && (
                      <button
                        type="button"
                        onClick={() => onExecute(row)}
                        style={{ ...actionBtnBase, ...executeBtn }}
                      >
                        Execute
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onEditPrice(row)}
                      style={{ ...actionBtnBase, ...editBtn }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(row.id)}
                      style={{ ...actionBtnBase, ...cancelBtn }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </MobileRecordCard>
            );
          })}
        </MobileRecordList>
      ) : (
        <div className="fx-book-table-scroll" style={{ border: "1px solid var(--border)", borderRadius: 6, overflow: "auto", maxHeight: "64vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--bg-secondary)" }}>
                <th style={{ textAlign: "left", padding: 8 }}>Time</th>
                <th style={{ textAlign: "left", padding: 8 }}>Client</th>
                <th style={{ textAlign: "left", padding: 8 }}>Segment</th>
                <th style={{ textAlign: "left", padding: 8 }}>Script</th>
                <th style={{ textAlign: "left", padding: 8 }}>Type</th>
                <th style={{ textAlign: "right", padding: 8 }}>Lot</th>
                <th style={{ textAlign: "right", padding: 8 }}>Qty</th>
                <th style={{ textAlign: "right", padding: 8 }}>Price</th>
                <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                {canModify && <th style={{ textAlign: "center", padding: 8 }}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    borderTop: "1px solid var(--border)",
                    background:
                      String(row.orderTypeName || "").toLowerCase() === "buy"
                        ? "rgba(34,197,94,0.06)"
                        : "rgba(239,68,68,0.06)",
                  }}
                >
                  <td style={{ padding: 8 }}>{formatUiDateTime(row.tradeDateTime || row.addTime, "—")}</td>
                  <td style={{ padding: 8 }}>{row.clientName}</td>
                  <td style={{ padding: 8 }}>
                    {resolveSegmentLabel(row.segmentId, segmentsList, row.segment)}
                  </td>
                  <td style={{ padding: 8 }}>{row.script}</td>
                  <td style={{ padding: 8 }}>{row.executionTypeName}</td>
                  <td style={{ padding: 8, textAlign: "right" }}>{Number(row.lot || 0).toFixed(2)}</td>
                  <td style={{ padding: 8, textAlign: "right" }}>{Number(row.qty || 0).toFixed(2)}</td>
                  <td style={{ padding: 8, textAlign: "right" }}>{Number(row.rate || 0).toFixed(2)}</td>
                  <td style={{ padding: 8 }}>{row.status}</td>
                  {canModify && (
                    <td style={{ padding: 8, textAlign: "center", whiteSpace: "nowrap" }}>
                      {row.status === "Pending" && (
                        <>
                          {isSuperAdmin && (
                            <button onClick={() => onExecute(row)} style={{ ...actionBtnBase, ...executeBtn }}>
                              Execute
                            </button>
                          )}
                          <button onClick={() => onEditPrice(row)} style={{ ...actionBtnBase, ...editBtn }}>
                            Edit
                          </button>
                          <button onClick={() => onDelete(row.id)} style={{ ...actionBtnBase, ...cancelBtn }}>
                            Cancel
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!orderBookPendingLoading && visibleRows.length === 0 && (
            <div style={{ textAlign: "center", padding: 16, color: "var(--text-muted)" }}>No orders found</div>
          )}
          {orderBookPendingLoading && (
            <div style={{ textAlign: "center", padding: 16, color: "var(--text-muted)" }}>Loading...</div>
          )}
        </div>
      )}

      <div />
    </div>
  );
}

const actionBtnBase = {
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.2,
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
  marginRight: 6,
  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
};

const editBtn = {
  background: "#0ea5e9",
  color: "#fff",
};

const cancelBtn = {
  background: "#ef4444",
  color: "#fff",
};

const executeBtn = {
  background: "#22c55e",
  color: "#fff",
};
